import React, { Component } from 'react';
import debounce from 'lodash/debounce';
import i18n from '@i18next/i18n';
import { IMAGE, ERROR } from '../../../../editor/src/img/pageIndex/index';
import styles from './fileupload.css';
import classNames from 'classnames';
import { IImageEditManager } from '@src/slide/application/service/copy-paste/core/cp-image.new/image-edit-manager/core/image-edit-manager.interface';
import { FILE_TYPE_IMAGE } from '@src/js/common/ark-extensions/dc-components/attachment-uploader';

const normalizeUrl = (url) => {
    if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) {
        return url;
    }
    return `http://${url}`;
};

interface FileUploadProps {
    maxSize: number;
    onChange: (image, contextData) => void;
    onClear?: () => void;
    getImageEditManager: () => Promise<IImageEditManager>
    imageName?: string;
    src?: string;
    placeholder?: string;
    contextData: object;
    useForEdit: boolean;
}

interface FileUploadState {
    sourceValue: string;
    invalid?: boolean;
    loading?: string|null;
}

export default class FileUpload extends Component<FileUploadProps, FileUploadState> {
    public static defaultProps = {
        contextData: {},
    };

    /*
     * Debounced source change handler, calls updateImage after 500ms of no activity.
     */

    private debouncedSourceChange = debounce((value, contextData) => {
        this.updateImage(value, contextData);
    }, 500);


    public constructor(props) {
        super(props);
        this.state = {
            sourceValue: '',
        };
    }

    /*
     * Clear sourceValue if we have a props.src
     */

    // eslint-disable-next-line react/no-deprecated
    public componentWillReceiveProps(nextProps) {
        if (nextProps.src) {
            this.setState({
                sourceValue: '',
            });
        }
    }

    public render() {
        return (
            <div>
                {this.props.src ? this.renderClearable() : this.renderInputs()}
            </div>
        );
    }

    /*
     * Get image dimensions by fetching image
     */

    private getImageDimensions(src, cb) {
        const imageElement = new Image();

        // success handler
        imageElement.addEventListener('load', () => {
            const { width, height } = imageElement;
            cb(null, {
                width,
                height,
            });
        });

        // error handler
        imageElement.addEventListener('error', () => {
            cb('Unable to read image');
        });

        // load image
        imageElement.src = src;
    }

    private handleImageUpload = async (ev) => {
        ev.persist();

        if (!ev.target.files) {
            return;
        }
        const files = Array.from(ev.target.files);

        let images;
        const imageEditManager = await this.props.getImageEditManager();
        if (imageEditManager.imageReplaceAction === 3) {
            // 文字的图片填充不走图片秒看逻辑，因为分支功能带来sdk处较为复杂的处理得不偿失
            images = await imageEditManager.uploadWithoutQuickLook(files as Blob[], i18n.t('正在填充图片...'));
        } else {
            images = await imageEditManager.createEditorImages(files as Blob[]);
        }

        this.props.onChange(images, { ...this.props.contextData });
        this.setState({ sourceValue: '' });
    };

    /*
     * Update image with URL. Fetches image dimensions then emits change if valid
     */

    private updateImage = (value, contextData) => {
        const src = normalizeUrl(value);

        // bail if less than 10 chars normalized
        if (src.length < 10) {
            return this.setState({
                invalid: false,
                loading: null,
            });
        }

        this.setState({ loading: src });

        // get dimensions then emit onChange
        this.getImageDimensions(src, (err, dimensions) => {
            // error fetching image
            if (err) {
                return this.setState({
                    invalid: true,
                    loading: null,
                });
            }

            // bail if input has changed since we started fetching
            if (value !== this.state.sourceValue || src !== this.state.loading) return;

            this.props.onChange({
                ...dimensions,
                imageName: null,
                src,
            }, contextData);

            this.setState({
                sourceValue: '',
                invalid: false,
                loading: null,
            });
        });
    };

    /*
     * Set internal state, then debounce event propagation
     */

    private handleSourceChange = (e) => {
        this.setState({
            sourceValue: e.target.value,
            invalid: false,
            loading: null,
        });
        this.debouncedSourceChange(e.target.value, { ...this.props.contextData });
    };

    /*
     * Cancel debounced handler, then updateImage.
     */

    private handleSourceBlur = (e) => {
        this.debouncedSourceChange.cancel();
        this.updateImage(e.target.value, { ...this.props.contextData });
    };

    /*
     * Handle clearing out the uploader
     */

    private handleClearClicked = () => {
        this.props.onClear?.();
    };

    private renderLoading = () => (
        <div className={styles.spinner}>
            <div className={styles.doubleBounce1}></div>
            <div className={styles.doubleBounce2}></div>
        </div>
    );

    /*
     * Render the inputs for uploading or entering in a URL
     */

    private renderInputs() {
        const inputClasses = classNames({
            globalInput: true,
            [styles.inputHasIcon]: this.state.invalid || this.state.loading,
        });

        return (
            !this.props.useForEdit
                ? <div>
                    <input
                        type="text"
                        className={inputClasses}
                        placeholder={this.props.placeholder}
                        onChange={this.handleSourceChange}
                        onBlur={this.handleSourceBlur}
                        value={this.state.sourceValue}
                    />
                    {this.state.invalid && !this.state.loading
                        ? <div
                            className={styles.errorIcon}
                            title="Unable to load image"
                        >
                            <img src={ERROR}/>
                        </div> : null}
                    {this.state.loading ? this.renderLoading() : null}
                    <div className={styles.breakHr}>
                        <div>OR</div>
                    </div>
                    <label className={`globalButton ${styles.fileUpload}`}>
                    Upload an image...
                        <input
                            className={styles.visuallyHidden}
                            type="file"
                            accept={FILE_TYPE_IMAGE}
                            multiple={true}
                            size={10}
                            onChange={this.handleImageUpload}
                        />
                    </label>
                </div>
                : <>
                    <input
                        className={styles.visuallyHidden}
                        id="select_pic_input"
                        type="file"
                        accept={FILE_TYPE_IMAGE}
                        multiple={true}
                        size={10}
                        onChange={this.handleImageUpload}
                        value={this.state.sourceValue}
                    />
                    <input
                        className={styles.visuallyHidden}
                        id="select_pic_input_local_video"
                        type="file"
                        accept="image/png, image/jpeg"
                        multiple={true}
                        size={10}
                        onChange={this.handleImageUpload}
                        value={this.state.sourceValue}
                    />
                </>
        );
    }

    /*
     * Render clearable state of the file uploader
     */

    private renderClearable() {
        const { imageName, src } = this.props;
        return (
            <div className={styles.form}>
                <div className={styles.uploadedFile}>
                    <span
                        className={styles.uploadedFileIcon}
                    >
                        <img src={IMAGE}/>
                    </span>
                    <span className={styles.uploadedFileName} title={imageName || src}>
                        {imageName || src}
                    </span>
                </div>
                <button
                    className={`globalButton ${styles.fileUpload} ${styles.removeButton}`}
                    onClick={this.handleClearClicked}
                >
                    Remove image
                </button>
            </div>
        );
    }
}
